import { Component, EventEmitter, forwardRef, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormFieldBaseComponent } from '../form-field-base/form-field-base.component';
import { Style } from '@app/core/services/core-component.service';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
    selector: 'tt-checkbox',
    templateUrl: './checkbox.component.html',
    styleUrls: ['./checkbox.component.css'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CheckboxComponent),
            multi: true,
        },
    ],
})
export class CheckboxComponent<TData> extends FormFieldBaseComponent implements OnChanges, OnInit, ControlValueAccessor {
    /**
     * The data model of the component, default is `'1'` for checked, and `'0'` for unchecked.
     * If `ttTrueValue` or `ttFalseValue` is given, the model will be either of those.
     */
    @Input()
    get ttModel(): TData {
        return this._model === true ? this.ttTrueValue : this.ttFalseValue;
    }
    set ttModel(value: TData) {
        this._model = value === this.ttTrueValue;
    }
    _model = false;

    /**
     * Event emitted when the checkbox has a new value.
     */
    @Output()
    ttModelChange = new EventEmitter<TData>();

    private onControlChange: (value: TData) => void = () => {};

    private onControlTouched: () => void = () => {};

    /**
     * The true value used when the checkbox is checked, default is `'1'`.
     */
    @Input()
    ttTrueValue: any = '1';

    /**
     * The false value used when the checkbox is unchecked, default is `'0'`.
     */
    @Input()
    ttFalseValue: any = '0';

    /**
     * Ids of the elements in the component.
     */
    public id = {
        checkbox: crypto.randomUUID(),
    };

    public onReadonlyInteraction(event: Event) {
        if (!this.ttReadonly) return;

        event.preventDefault();
        event.stopPropagation();
    }

    public onModelChange(event: boolean) {
        if (this.ttReadonly) return;

        this._model = event;
        this.ttModel = event === true ? this.ttTrueValue : this.ttFalseValue;
        this.rememberSubject.next(this.ttModel);
        this.ttModelChange.emit(this.ttModel);
        this.onControlChange(this.ttModel);
    }

    public onBlur() {
        this.onControlTouched();
    }

    public writeValue(value: TData | null | undefined): void {
        this.ttModel = value ?? this.ttFalseValue;
    }

    public registerOnChange(fn: (value: TData) => void): void {
        this.onControlChange = fn;
    }

    public registerOnTouched(fn: () => void): void {
        this.onControlTouched = fn;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.ttDisabled = isDisabled;
    }

    public override style: Style = {
        checkbox: {},
    };

    public override setStyle(ttStyle = this.ttStyle) {
        super.setStyle(ttStyle);
        this.style = this.coreComponentService.setStyle({ style: this.style, ttStyle: ttStyle ?? {}, mainElement: 'checkbox' });
    }

    private async getLastModelState() {
        if (!this.ttOnlyRemember && this.ttRememberId) {
            this.ttModel = (await this.remember.getLastStatus(this.ttRememberId))[0].variablevalue;
        }
    }

    override ngOnInit(): void {
        super.ngOnInit();

        if (!this.ttOnlyRemember && this.ttRememberId) {
            this.getLastModelState();
        }
    }

    override async ngOnChanges(changes: SimpleChanges): Promise<void> {
        super.ngOnChanges(changes);

        if (changes?.['ttModel']) {
            this.ttModel = changes['ttModel'].currentValue;
        }
    }
}
